// AGGREGATION & FALLBACK LAYER ONLY — not the primary booking system.
// Real booking flows use existing patient-facing pages with API integration:
//   Doctor → Marketplace → ProviderDetail → BookDoctorForm (patientApi.bookDoctor)
//   Lab    → PatientLabDiscovery → PatientLabBooking (patientApi.lab.*)
//   Imaging → ImagingDiscovery → ImagingCenter → ImagingBooking (patientApi.getMarketplaceProvider*, booking stored locally)
//   Pharmacy → Marketplace → BookPharmacyForm (patientApi.bookPharmacy)
// This store provides: timeline events, notifications, access logs,
// temporary UI state, and offline placeholder state when backend/API is missing.

type AppointmentStatus = 'BOOKED' | 'CONFIRMED' | 'COMPLETED' | 'CANCELLED' | 'NO_SHOW';
type LabOrderStatus = 'BOOKED' | 'SAMPLE_COLLECTED' | 'PROCESSING' | 'COMPLETED' | 'CANCELLED';
type ImagingOrderStatus = 'BOOKED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';
type PharmacyOrderStatus = 'PLACED' | 'CONFIRMED' | 'DISPATCHED' | 'DELIVERED' | 'CANCELLED';
type ClaimStatus = 'SUBMITTED' | 'UNDER_REVIEW' | 'APPROVED' | 'REJECTED' | 'SETTLED';
type ConsentStatus = 'ACTIVE' | 'REVOKED' | 'EXPIRED';
type TimelineEventType = 'appointment' | 'lab' | 'imaging' | 'pharmacy' | 'prescription' | 'emergency' | 'consent' | 'access' | 'insurance' | 'medicine' | 'record' | 'general';
type NotificationType = 'appointment' | 'lab' | 'imaging' | 'pharmacy' | 'prescription' | 'emergency' | 'consent' | 'insurance' | 'medicine' | 'general';

export interface Appointment {
  id: string;
  doctorName: string;
  specialty: string;
  hospital: string;
  date: string;
  time: string;
  type: 'In-Person' | 'Telemedicine';
  status: AppointmentStatus;
  notes?: string;
  createdAt: string;
}

export interface LabOrder {
  id: string;
  testName: string;
  labName: string;
  date: string;
  time: string;
  collectionType: 'Walk-in' | 'Home Collection';
  status: LabOrderStatus;
  reportUrl?: string;
  results?: string;
  createdAt: string;
  /** Backend-issued report number. Only present for orders fetched from the
   *  real lab API — never synthesize one for locally-created orders. */
  reportNumber?: string;
}

export interface ImagingOrder {
  id: string;
  type: 'X-Ray' | 'CT Scan' | 'MRI' | 'Ultrasound' | 'PET Scan' | 'Mammography';
  bodyPart: string;
  center: string;
  date: string;
  time: string;
  status: ImagingOrderStatus;
  reportUrl?: string;
  findings?: string;
  createdAt: string;
}

type PaymentMethod = 'UPI' | 'CARD' | 'NET_BANKING' | 'COD' | 'WALLET' | 'INSURANCE';
type PaymentStatus = 'PENDING' | 'COMPLETED' | 'FAILED' | 'REFUNDED';

export interface PharmacyOrder {
  id: string;
  medicines: { name: string; qty: number; dosage: string; unitPrice?: number }[];
  pharmacyName: string;
  status: PharmacyOrderStatus;
  prescriptionId?: string;
  totalAmount: number;
  deliveryFee?: number;
  discount?: number;
  paymentMethod?: PaymentMethod;
  paymentStatus?: PaymentStatus;
  deliveryMode?: 'HOME' | 'PICKUP';
  deliveryAddress?: string;
  receiptId?: string;
  createdAt: string;
}

export interface PharmacyReceipt {
  id: string;
  orderId: string;
  pharmacyName: string;
  medicines: { name: string; qty: number; unitPrice: number; subtotal: number }[];
  subtotal: number;
  deliveryFee: number;
  discount: number;
  tax: number;
  total: number;
  paymentMethod: PaymentMethod;
  paymentStatus: PaymentStatus;
  createdAt: string;
}

export interface Prescription {
  id: string;
  doctorName: string;
  hospital: string;
  date: string;
  medicines: { name: string; dosage: string; frequency: string; duration: string }[];
  diagnosis?: string;
  appointmentId?: string;
  createdAt: string;
}

export interface MedicineReminder {
  id: string;
  name: string;
  dosage: string;
  frequency: string;
  times: string[];
  startDate: string;
  endDate?: string;
  instructions?: string;
  beforeAfterFood?: 'before' | 'after' | 'any';
  prescriptionId?: string;
  takenLog?: { date: string; time: string; taken: boolean; reason?: string }[];
  active: boolean;
  createdAt: string;
}

export interface AmbulanceRequest {
  id: string;
  emergencyType: string;
  ambulanceType?: string;
  location: string;
  destination?: string;
  notes?: string;
  status: 'REQUESTED' | 'DISPATCHED' | 'EN_ROUTE' | 'ARRIVED' | 'COMPLETED' | 'CANCELLED';
  createdAt: string;
}

export interface ConsentGrant {
  id: string;
  recipientName: string;
  recipientRole: string;
  scopes: string[];
  validUntil: string;
  status: ConsentStatus;
  recordIds?: string[];
  createdAt: string;
  revokedAt?: string;
}

export interface AccessLogEntry {
  id: string;
  accessor: string;
  accessorRole: string;
  action: string;
  recordType: string;
  recordId?: string;
  timestamp: string;
  purpose?: string;
}

export interface InsuranceClaim {
  id: string;
  policyNumber: string;
  type: 'Claim' | 'Preauth';
  amount: number;
  description: string;
  hospital?: string;
  serviceType?: string;
  status: ClaimStatus;
  documents: string[];
  createdAt: string;
}

export interface TimelineEvent {
  id: string;
  type: TimelineEventType;
  title: string;
  description?: string;
  date: string;
  status?: string;
  linkedRoute?: string;
  linkedId?: string;
}

export interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  date: string;
  read: boolean;
  route?: string;
}

interface StoreState {
  appointments: Appointment[];
  labOrders: LabOrder[];
  imagingOrders: ImagingOrder[];
  pharmacyOrders: PharmacyOrder[];
  pharmacyReceipts: PharmacyReceipt[];
  prescriptions: Prescription[];
  medicineReminders: MedicineReminder[];
  ambulanceRequests: AmbulanceRequest[];
  consentGrants: ConsentGrant[];
  accessLogs: AccessLogEntry[];
  insuranceClaims: InsuranceClaim[];
  timeline: TimelineEvent[];
  notifications: Notification[];
  appLock: { enabled: boolean; autoLockTiming: string };
}

const STORAGE_KEY = 'nhc_patient_production_store';

function uid(): string {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
}

function now(): string {
  return new Date().toISOString();
}

function todayStr(): string {
  return new Date().toISOString().split('T')[0];
}

function loadState(): StoreState {
  const defaults: StoreState = {
    appointments: [],
    labOrders: [],
    imagingOrders: [],
    pharmacyOrders: [],
    pharmacyReceipts: [],
    prescriptions: [],
    medicineReminders: [],
    ambulanceRequests: [],
    consentGrants: [],
    accessLogs: [],
    insuranceClaims: [],
    timeline: [],
    notifications: [],
    appLock: { enabled: false, autoLockTiming: 'immediately' },
  };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return { ...defaults, ...JSON.parse(raw) };
  } catch { /* ignore */ }
  return defaults;
}

function saveState(state: StoreState) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (e) {
    // Quota exceeded or storage disabled — the in-memory state for this
    // session is still correct, but it won't survive a reload. Log so this
    // is visible instead of silently losing data on the next refresh.
    console.warn('[patientProductionStore] Failed to persist state:', e);
  }
}

let _state = loadState();
const _listeners: Set<() => void> = new Set();

function emit() {
  saveState(_state);
  _listeners.forEach(fn => fn());
}

export const store = {
  subscribe(fn: () => void) { _listeners.add(fn); return () => { _listeners.delete(fn); }; },
  getState(): StoreState { return _state; },

  // ─── Appointments ───
  // create* accept an optional `id`: pass the server id when the backend
  // create succeeded so the local mirror shares it and pages that merge
  // store+API can dedupe. A server-backed create also skips the local
  // timeline event — the backend audit log already feeds /patient/timeline,
  // so a local copy would show the same action twice.
  createAppointment(data: Omit<Appointment, 'id' | 'status' | 'createdAt'> & { id?: string }): Appointment {
    const appt: Appointment = { ...data, id: data.id || uid(), status: 'BOOKED', createdAt: now() };
    _state = { ..._state, appointments: [appt, ..._state.appointments] };
    if (!data.id) addTimelineEvent('appointment', `Appointment booked with ${appt.doctorName}`, `${appt.specialty} — ${appt.type}`, `/patient/appointments`, appt.id);
    addNotification('appointment', appt.type === 'Telemedicine' ? 'Telemedicine Booked' : 'Appointment Booked', `Your ${appt.type === 'Telemedicine' ? 'telemedicine consultation' : 'appointment'} with ${appt.doctorName} on ${appt.date} at ${appt.time} has been booked.`, appt.type === 'Telemedicine' ? '/patient/care-services/telemedicine' : '/patient/appointments');
    emit();
    return appt;
  },
  updateAppointmentStatus(id: string, status: AppointmentStatus) {
    _state = { ..._state, appointments: _state.appointments.map(a => a.id === id ? { ...a, status } : a) };
    emit();
  },
  getAppointments(): Appointment[] { return _state.appointments; },

  // ─── Lab Orders ───
  createLabOrder(data: Omit<LabOrder, 'id' | 'status' | 'createdAt'> & { id?: string }): LabOrder {
    const order: LabOrder = { ...data, id: data.id || uid(), status: 'BOOKED', createdAt: now() };
    _state = { ..._state, labOrders: [order, ..._state.labOrders] };
    if (!data.id) addTimelineEvent('lab', `Lab test booked: ${order.testName}`, `${order.labName} — ${order.collectionType}`, `/patient/lab-reports`, order.id);
    addNotification('lab', 'Lab Test Booked', `${order.testName} at ${order.labName} on ${order.date} at ${order.time}.`, '/patient/lab-reports');
    emit();
    return order;
  },
  updateLabOrderStatus(id: string, status: LabOrderStatus, results?: string) {
    _state = { ..._state, labOrders: _state.labOrders.map(o => o.id === id ? { ...o, status, ...(results ? { results } : {}) } : o) };
    if (status === 'COMPLETED') {
      const o = _state.labOrders.find(o => o.id === id);
      if (o) addNotification('lab', 'Lab Report Ready', `Results for ${o.testName} are now available.`, '/patient/lab-reports');
    }
    emit();
  },
  getLabOrders(): LabOrder[] { return _state.labOrders; },

  // ─── Imaging Orders ───
  createImagingOrder(data: Omit<ImagingOrder, 'id' | 'status' | 'createdAt'> & { id?: string }): ImagingOrder {
    const order: ImagingOrder = { ...data, id: data.id || uid(), status: 'BOOKED', createdAt: now() };
    _state = { ..._state, imagingOrders: [order, ..._state.imagingOrders] };
    addTimelineEvent('imaging', `Imaging booked: ${order.type} — ${order.bodyPart}`, `${order.center}`, `/patient/diagnostics`, order.id);
    addNotification('imaging', 'Imaging Booked', `${order.type} (${order.bodyPart}) at ${order.center} on ${order.date}.`, '/patient/diagnostics');
    emit();
    return order;
  },
  updateImagingOrderStatus(id: string, status: ImagingOrderStatus, findings?: string) {
    _state = { ..._state, imagingOrders: _state.imagingOrders.map(o => o.id === id ? { ...o, status, ...(findings ? { findings } : {}) } : o) };
    if (status === 'COMPLETED') {
      const o = _state.imagingOrders.find(o => o.id === id);
      if (o) addNotification('imaging', 'Imaging Report Ready', `${o.type} results for ${o.bodyPart} are now available.`, '/patient/diagnostics');
    }
    emit();
  },
  getImagingOrders(): ImagingOrder[] { return _state.imagingOrders; },

  // ─── Pharmacy Orders ───
  createPharmacyOrder(data: Omit<PharmacyOrder, 'id' | 'status' | 'createdAt'> & { id?: string }): PharmacyOrder {
    const order: PharmacyOrder = { ...data, id: data.id || uid(), status: 'PLACED', createdAt: now() };
    _state = { ..._state, pharmacyOrders: [order, ..._state.pharmacyOrders] };
    const payLabel = order.paymentMethod ? ` · ${order.paymentMethod}` : '';
    if (!data.id) addTimelineEvent('pharmacy', `Pharmacy order placed`, `${order.medicines.length} item(s) from ${order.pharmacyName}${payLabel} · ₹${order.totalAmount}`, `/patient/pharmacy`, order.id);
    addNotification('pharmacy', 'Order Placed', `Your order from ${order.pharmacyName} has been placed. Total: ₹${order.totalAmount}`, '/patient/pharmacy');
    emit();
    return order;
  },
  updatePharmacyOrderStatus(id: string, status: PharmacyOrderStatus) {
    _state = { ..._state, pharmacyOrders: _state.pharmacyOrders.map(o => o.id === id ? { ...o, status } : o) };
    if (status === 'DELIVERED') {
      const o = _state.pharmacyOrders.find(o => o.id === id);
      if (o) addNotification('pharmacy', 'Order Delivered', `Your order from ${o.pharmacyName} has been delivered.`, '/patient/pharmacy');
    }
    emit();
  },
  getPharmacyOrders(): PharmacyOrder[] { return _state.pharmacyOrders; },

  // ─── Pharmacy Receipts ───
  createPharmacyReceipt(data: Omit<PharmacyReceipt, 'id' | 'createdAt'>): PharmacyReceipt {
    const receipt: PharmacyReceipt = { ...data, id: uid(), createdAt: now() };
    _state = { ..._state, pharmacyReceipts: [receipt, ..._state.pharmacyReceipts] };
    // Link receipt to order
    _state = { ..._state, pharmacyOrders: _state.pharmacyOrders.map(o => o.id === data.orderId ? { ...o, receiptId: receipt.id, paymentStatus: data.paymentStatus } : o) };
    addTimelineEvent('pharmacy', 'Pharmacy receipt generated', `Receipt #${receipt.id.slice(0, 8)} · ₹${receipt.total} · ${receipt.paymentMethod}`, '/patient/pharmacy', receipt.id);
    addNotification('pharmacy', 'Receipt Generated', `Receipt for your order from ${receipt.pharmacyName} — ₹${receipt.total}`, '/patient/pharmacy');
    emit();
    return receipt;
  },
  getPharmacyReceipts(): PharmacyReceipt[] { return _state.pharmacyReceipts; },

  // ─── Prescriptions ───
  createPrescription(data: Omit<Prescription, 'id' | 'createdAt'>): Prescription {
    const rx: Prescription = { ...data, id: uid(), createdAt: now() };
    _state = { ..._state, prescriptions: [rx, ..._state.prescriptions] };
    addTimelineEvent('prescription', `Prescription by ${rx.doctorName}`, rx.diagnosis || `${rx.medicines.length} medicine(s)`, `/patient/prescriptions`, rx.id);
    addNotification('prescription', 'New Prescription', `Prescription from ${rx.doctorName} — ${rx.medicines.length} medicine(s).`, '/patient/prescriptions');
    emit();
    return rx;
  },
  getPrescriptions(): Prescription[] { return _state.prescriptions; },

  // ─── Medicine Reminders ───
  createMedicineReminder(data: Omit<MedicineReminder, 'id' | 'active' | 'createdAt'>): MedicineReminder {
    const rem: MedicineReminder = { ...data, id: uid(), active: true, createdAt: now() };
    _state = { ..._state, medicineReminders: [rem, ..._state.medicineReminders] };
    addTimelineEvent('medicine', `Medicine reminder set: ${rem.name}`, `${rem.dosage} — ${rem.frequency}`, `/patient/medicines`, rem.id);
    addNotification('medicine', 'Reminder Set', `Medicine reminder for ${rem.name} (${rem.dosage}) has been set.`, '/patient/medicines');
    emit();
    return rem;
  },
  toggleMedicineReminder(id: string) {
    _state = { ..._state, medicineReminders: _state.medicineReminders.map(r => r.id === id ? { ...r, active: !r.active } : r) };
    emit();
  },
  deleteMedicineReminder(id: string) {
    _state = { ..._state, medicineReminders: _state.medicineReminders.filter(r => r.id !== id) };
    emit();
  },
  logMedicineDose(id: string, time: string, taken: boolean, reason?: string) {
    const today = todayStr();
    _state = {
      ..._state,
      medicineReminders: _state.medicineReminders.map(r => {
        if (r.id !== id) return r;
        const log = r.takenLog || [];
        return { ...r, takenLog: [...log, { date: today, time, taken, reason }] };
      }),
    };
    const rem = _state.medicineReminders.find(r => r.id === id);
    if (rem) {
      const action = taken ? 'taken' : 'skipped';
      const desc = reason ? `${rem.dosage} at ${time} — ${reason}` : `${rem.dosage} at ${time}`;
      addTimelineEvent('medicine', `Medicine ${action}: ${rem.name}`, desc, '/patient/medicines', rem.id);
      if (!taken) {
        addNotification('medicine', 'Dose Skipped', `${rem.name} ${rem.dosage} at ${time} was skipped${reason ? `: ${reason}` : ''}.`, '/patient/medicines');
      }
    }
    emit();
  },
  getMedicineReminders(): MedicineReminder[] { return _state.medicineReminders; },

  // ─── Ambulance / Emergency ───
  // Mirror of a SERVER-recorded emergency request — callers must only invoke
  // this after the backend accepted the request, passing the server id so the
  // mirror and any server-side reads stay in sync. The notification says
  // "recorded", never "dispatched": no dispatch integration exists, and no
  // fake access-log entry is written (the backend audit-logs the SOS itself).
  createAmbulanceRequest(data: Omit<AmbulanceRequest, 'id' | 'status' | 'createdAt'> & { id?: string }): AmbulanceRequest {
    const req: AmbulanceRequest = { ...data, id: data.id || uid(), status: 'REQUESTED', createdAt: now() };
    _state = { ..._state, ambulanceRequests: [req, ..._state.ambulanceRequests] };
    const desc = [req.ambulanceType || 'BLS', req.location, req.destination ? `→ ${req.destination}` : ''].filter(Boolean).join(' · ');
    addTimelineEvent('emergency', `Emergency: ${req.emergencyType}`, desc, `/patient/emergency`, req.id);
    addNotification('emergency', 'Emergency Request Recorded', `${req.emergencyType} — ${req.ambulanceType || 'BLS'} ambulance requested. Pickup: ${req.location}. In a real emergency call 112/108.`, '/patient/care-services/ambulance');
    emit();
    return req;
  },
  updateAmbulanceStatus(id: string, status: AmbulanceRequest['status']) {
    _state = { ..._state, ambulanceRequests: _state.ambulanceRequests.map(r => r.id === id ? { ...r, status } : r) };
    emit();
  },
  getAmbulanceRequests(): AmbulanceRequest[] { return _state.ambulanceRequests; },

  // ─── Consent Grants ───
  // Consent has no backend timeline action, so its local timeline event is
  // kept even for server-backed creates.
  createConsentGrant(data: Omit<ConsentGrant, 'id' | 'status' | 'createdAt'> & { id?: string }): ConsentGrant {
    const grant: ConsentGrant = { ...data, id: data.id || uid(), status: 'ACTIVE', createdAt: now() };
    _state = { ..._state, consentGrants: [grant, ..._state.consentGrants] };
    addTimelineEvent('consent', `Record shared with ${grant.recipientName}`, `${grant.scopes.join(', ')}`, `/patient/consent`, grant.id);
    addNotification('consent', 'Record Shared', `Records shared with ${grant.recipientName} (${grant.recipientRole}).`, '/patient/consent');
    addAccessLogEntry(grant.recipientName, grant.recipientRole, 'CONSENT_GRANTED', 'Medical Records', grant.id, 'Patient consent granted');
    emit();
    return grant;
  },
  revokeConsentGrant(id: string) {
    const grant = _state.consentGrants.find(g => g.id === id);
    _state = { ..._state, consentGrants: _state.consentGrants.map(g => g.id === id ? { ...g, status: 'REVOKED' as ConsentStatus, revokedAt: now() } : g) };
    if (grant) {
      addTimelineEvent('consent', `Consent revoked for ${grant.recipientName}`, 'Access revoked', `/patient/access-logs`);
      addNotification('consent', 'Consent Revoked', `Access for ${grant.recipientName} has been revoked.`, '/patient/access-logs');
      addAccessLogEntry(grant.recipientName, grant.recipientRole, 'CONSENT_REVOKED', 'Medical Records', grant.id, 'Patient consent revoked');
    }
    emit();
  },
  getConsentGrants(): ConsentGrant[] { return _state.consentGrants; },

  // ─── Access Logs ───
  getAccessLogs(): AccessLogEntry[] { return _state.accessLogs; },

  // ─── Insurance Claims ───
  // `id` should be the SERVER claim id whenever the claim was accepted by the
  // backend — the mirror then dedupes against API reads instead of doubling.
  createInsuranceClaim(data: Omit<InsuranceClaim, 'id' | 'status' | 'createdAt'> & { id?: string }): InsuranceClaim {
    const claim: InsuranceClaim = { ...data, id: data.id || uid(), status: 'SUBMITTED', createdAt: now() };
    _state = { ..._state, insuranceClaims: [claim, ..._state.insuranceClaims] };
    const desc = [claim.hospital, claim.serviceType, `₹${claim.amount}`].filter(Boolean).join(' · ');
    addTimelineEvent('insurance', `${claim.type} submitted`, desc || `₹${claim.amount} — ${claim.description}`, `/patient/insurance`, claim.id);
    addNotification('insurance', `${claim.type} Submitted`, `Your ${claim.type.toLowerCase()} for ₹${claim.amount}${claim.hospital ? ` at ${claim.hospital}` : ''} has been submitted.`, '/patient/insurance');
    emit();
    return claim;
  },
  updateInsuranceClaimStatus(id: string, status: ClaimStatus) {
    _state = { ..._state, insuranceClaims: _state.insuranceClaims.map(c => c.id === id ? { ...c, status } : c) };
    emit();
  },
  getInsuranceClaims(): InsuranceClaim[] { return _state.insuranceClaims; },

  // ─── Timeline ───
  getTimeline(): TimelineEvent[] { return _state.timeline; },

  // ─── Notifications ───
  getNotifications(): Notification[] { return _state.notifications; },
  markNotificationRead(id: string) {
    _state = { ..._state, notifications: _state.notifications.map(n => n.id === id ? { ...n, read: true } : n) };
    emit();
  },
  markAllNotificationsRead() {
    _state = { ..._state, notifications: _state.notifications.map(n => ({ ...n, read: true })) };
    emit();
  },
  getUnreadCount(): number { return _state.notifications.filter(n => !n.read).length; },

  // ─── App Lock ───
  setAppLock(enabled: boolean) {
    _state = { ..._state, appLock: { ..._state.appLock, enabled } };
    emit();
  },
  setAutoLockTiming(timing: string) {
    _state = { ..._state, appLock: { ..._state.appLock, autoLockTiming: timing } };
    emit();
  },
  getAppLock() { return _state.appLock; },

  // ─── Reset (dev only) ───
  reset() {
    localStorage.removeItem(STORAGE_KEY);
    _state = loadState();
    emit();
  },
};

function addTimelineEvent(type: TimelineEventType, title: string, description?: string, linkedRoute?: string, linkedId?: string) {
  const event: TimelineEvent = { id: uid(), type, title, description, date: now(), status: 'active', linkedRoute, linkedId };
  _state = { ..._state, timeline: [event, ..._state.timeline] };
}

function addNotification(type: NotificationType, title: string, message: string, route?: string) {
  const notif: Notification = { id: uid(), type, title, message, date: now(), read: false, route };
  _state = { ..._state, notifications: [notif, ..._state.notifications] };
}

function addAccessLogEntry(accessor: string, accessorRole: string, action: string, recordType: string, recordId?: string, purpose?: string) {
  const entry: AccessLogEntry = { id: uid(), accessor, accessorRole, action, recordType, recordId, timestamp: now(), purpose };
  _state = { ..._state, accessLogs: [entry, ..._state.accessLogs] };
}

// React hook
import { useSyncExternalStore } from 'react';

export function useStore<T>(selector: (s: StoreState) => T): T {
  return useSyncExternalStore(
    store.subscribe,
    () => selector(store.getState()),
  );
}
